import { useEffect, useRef, useState } from 'react';
import { Sparkles, X, Lock, Download, Clock } from 'lucide-react';

/**
 * The one question Review Assist asks, and the last time it asks it.
 *
 * It is a question rather than a default because it costs a 15 MB download and
 * several minutes of the photographer's GPU, and because the honest answer for
 * a lot of shoots is "not worth it". Declining is a first-class outcome: the
 * shoot page keeps the offer, so "not now" never means "never again".
 */
export default function ReviewAssistPrompt({
  photoCount,
  onAnswer,
}: {
  photoCount: number;
  onAnswer: (choice: 'deep' | 'declined', always: boolean) => void;
}) {
  const [always, setAlways] = useState(false);
  const accept = useRef<HTMLButtonElement>(null);

  useEffect(() => accept.current?.focus(), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onAnswer('declined', false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onAnswer]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/80 p-5 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assist-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-ink-800 bg-ink-900 p-7 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          {/* Teal, because teal is what the badge it produces looks like. */}
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-pick-soft text-pick">
            <Sparkles size={18} />
          </span>
          <button
            className="-mr-1 -mt-1 rounded-lg p-1.5 text-ink-500 transition hover:bg-ink-850 hover:text-ink-200"
            onClick={() => onAnswer('declined', false)}
            aria-label="Not now"
          >
            <X size={16} />
          </button>
        </div>

        <h2 id="assist-title" className="mt-5 text-head font-semibold text-ink-50">
          Want the sharpest of each burst marked?
        </h2>

        {/* Only what the title does not already say. */}
        <p className="mt-3 text-body leading-relaxed text-ink-400">
          Photos you shot back to back get stacked into one, and the blurry ones get flagged.
        </p>

        {/*
          The one sentence that decides whether anybody says yes, given its own
          rule and its own colour. It used to be the fourth sentence of the
          paragraph above, where it read as a footnote rather than as the
          promise it is.
        */}
        <p className="mt-4 border-l-2 border-pick-line py-0.5 pl-3.5 text-body leading-relaxed text-ink-100">
          It only suggests. <span className="text-ink-300">You still keep or reject every photo
          yourself.</span>
        </p>

        {/* Three facts, labelled. The old version had them as three grey
            sentences of equal weight, which is a paragraph wearing a list. */}
        <ul className="mt-6 flex flex-col gap-3">
          <Fact icon={Lock} label="Stays on your computer">
            Nothing is uploaded, here or anywhere else.
          </Fact>
          <Fact icon={Download} label="One 15 MB download">
            The first time only. Never again.
          </Fact>
          <Fact icon={Clock} label={estimate(photoCount)}>
            <span className="tnum">For {photoCount.toLocaleString()} photos.</span> Slower without
            a graphics card.
          </Fact>
        </ul>

        <p className="mt-4 text-caption text-ink-500">
          You can carry on culling while it works.
        </p>

        <label className="mt-6 flex cursor-pointer items-center gap-2.5 text-caption text-ink-400">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-700 bg-ink-850 accent-ink-100"
            checked={always}
            onChange={(event) => setAlways(event.target.checked)}
          />
          Do this for every shoot without asking
        </label>

        <div className="mt-7 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <button className="btn-ghost justify-center px-5 py-2.5 text-body" onClick={() => onAnswer('declined', false)}>
            Not now
          </button>
          <button
            ref={accept}
            className="btn-primary justify-center px-5 py-2.5 text-body"
            onClick={() => onAnswer('deep', always)}
          >
            <Sparkles size={15} />
            Mark them
          </button>
        </div>
      </div>
    </div>
  );
}

/** One labelled fact: icon, the thing itself, then the detail underneath. */
function Fact({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Lock;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-3">
      <Icon size={15} className="mt-0.5 shrink-0 text-ink-500" />
      <span className="min-w-0">
        <span className="block text-caption font-medium text-ink-100">{label}</span>
        <span className="block text-caption leading-relaxed text-ink-500">{children}</span>
      </span>
    </li>
  );
}

/**
 * A rough wall-clock estimate, deliberately rough.
 *
 * 55 ms a frame is what the timing spike measured on WebGPU; the WASM fallback
 * came in at 298 ms, more than five times slower. Quoting the fast figure and
 * saying so beats quoting an average that is wrong for everybody — and the
 * sentence this feeds names the slower case explicitly.
 */
function estimate(photos: number): string {
  const minutes = (photos * 0.055) / 60;
  if (minutes < 0.75) return 'A few seconds';
  if (minutes < 60) return `About ${Math.ceil(minutes)} minutes`;
  return `About ${Math.round(minutes / 60)} hours`;
}
